/**
 * Data masking utilities for Kenya Data Protection Act compliance.
 *
 * These functions ensure that sensitive personal data (phone numbers and
 * identification numbers) are masked for non-admin roles.
 */

const VISIBLE_SUFFIX_LEN = 2;

/**
 * Masks the middle digits of a phone number, preserving the network prefix
 * and the last two digits.
 *
 * Examples:
 *   +254712345678  →  +254712XXXX78
 *   0712345678     →  0712XXXX78
 *
 * @param {string} phoneNumber
 * @returns {string}
 */
export function maskPhoneNumber(phoneNumber) {
  if (!phoneNumber || typeof phoneNumber !== "string") return phoneNumber;
  const cleaned = phoneNumber.trim();
  if (cleaned.length < 6) return cleaned;

  // Show up to 7 chars prefix (covers "+254712"), but at least 4 chars,
  // leaving a minimum of 2 chars to mask.
  const prefixLen = Math.min(7, Math.max(4, cleaned.length - 6));
  if (cleaned.length <= prefixLen + VISIBLE_SUFFIX_LEN) return cleaned;

  const maskLen = cleaned.length - prefixLen - VISIBLE_SUFFIX_LEN;
  return (
    cleaned.slice(0, prefixLen) +
    "X".repeat(maskLen) +
    cleaned.slice(-VISIBLE_SUFFIX_LEN)
  );
}

/**
 * Masks the middle digits of a national identification number, preserving
 * the first two and last two characters.
 *
 * Example:
 *   12345678  →  12XXXX78
 *
 * @param {string} idNumber
 * @returns {string}
 */
export function maskIdentificationNumber(idNumber) {
  if (!idNumber || typeof idNumber !== "string") return idNumber;
  const cleaned = idNumber.trim();
  if (cleaned.length < 5) return cleaned;

  const visiblePrefix = 2;
  const maskLen = cleaned.length - visiblePrefix - VISIBLE_SUFFIX_LEN;
  if (maskLen <= 0) return cleaned;

  return (
    cleaned.slice(0, visiblePrefix) +
    "X".repeat(maskLen) +
    cleaned.slice(-VISIBLE_SUFFIX_LEN)
  );
}

/**
 * Returns true when the given role should see masked data.
 * Only the CompanyAdmin role views full unmasked data.
 *
 * @param {string|null|undefined} userRole
 * @returns {boolean}
 */
export function shouldMaskData(userRole) {
  return userRole !== "CompanyAdmin";
}
